import {PammOfferType} from '../../../graphql/types'
import {OfferNode} from '../types'

/**
 * Extracts the agent chain of an offer as an ordered array of agent IDs.
 *
 * The backend stores the chain ordered `[ownAgent, parentAgent, grandparentAgent, ...]`,
 * so index 0 is the agent that owns the offer and each following entry walks one level
 * up the hierarchy. An empty array marks a Money Manager's own offer.
 */
export const getOfferChainIds = (offer: PammOfferType): number[] =>
  (offer.settings?.agentChain ?? [])
    .map((agent) => agent?.agentId)
    .filter((agentId): agentId is number => typeof agentId === 'number')

/** Serialises an agent chain into a stable map key. */
const toChainKey = (chainIds: number[]): string => chainIds.join(',')

/** Deterministic ordering by offer id so the tree renders identically on every load. */
const byOfferId = (a: OfferNode, b: OfferNode): number => (a.offer.id ?? 0) - (b.offer.id ?? 0)

/** Walks the tree assigning each node its depth relative to the rendered root. */
const assignLevels = (node: OfferNode, level: number): void => {
  node.level = level
  node.children.sort(byOfferId)
  node.children.forEach((child) => assignLevels(child, level + 1))
}

/**
 * Reconstructs the hierarchical PAMM offers tree from a flat list of offers.
 *
 * An offer's parent is the offer whose agent chain equals this offer's chain with the
 * first (own) agent removed — e.g. a sub-agent offer `[B, A]` hangs under agent offer `[A]`,
 * which in turn hangs under the Money Manager offer `[]`. Offers whose parent is not part
 * of the supplied data set (the agent themselves in the Agent view, or any gap in the data)
 * are promoted to roots. The result is safe to render recursively: chains are strictly
 * shortening towards the root, so no cycles are possible.
 *
 * @param offers - Flat list of offers in the viewer's scope (order does not matter).
 * @returns Root nodes, each carrying its depth-based `level` and nested `children`.
 */
export const buildOfferTree = (offers: PammOfferType[]): OfferNode[] => {
  const seenIds = new Set<number>()
  const nodes: OfferNode[] = []
  offers.forEach((offer) => {
    if (typeof offer.id === 'number') {
      if (seenIds.has(offer.id)) return
      seenIds.add(offer.id)
    }
    nodes.push({offer, level: 0, children: []})
  })

  // Index every node by its own chain key so a child can look its parent up in O(1).
  const nodesByChainKey = new Map<string, OfferNode[]>()
  nodes.forEach((node) => {
    const key = toChainKey(getOfferChainIds(node.offer))
    const bucket = nodesByChainKey.get(key)
    if (bucket) bucket.push(node)
    else nodesByChainKey.set(key, [node])
  })
  nodesByChainKey.forEach((bucket) => bucket.sort(byOfferId))

  const roots: OfferNode[] = []
  nodes.forEach((node) => {
    const chainIds = getOfferChainIds(node.offer)
    if (chainIds.length === 0) {
      roots.push(node)
      return
    }
    const parentKey = toChainKey(chainIds.slice(1))
    // When an agent owns several offers at the same chain position we attach the child to
    // the lowest-id parent for a deterministic tree.
    const parent = (nodesByChainKey.get(parentKey) ?? []).find((candidate) => candidate !== node)
    if (parent) parent.children.push(node)
    else roots.push(node)
  })

  roots.sort(byOfferId)
  roots.forEach((root) => assignLevels(root, 0))
  return roots
}
